// Loader del widget Yatendi — el script que el cliente pega en su sitio.
//
//   <script async src="https://widget.yatendi.com/v1.js" data-tenant="pk_xyz">
//   </script>
//
// Responsabilidades del loader:
//   1) Leer la config del propio <script> (data-tenant, data-* opcionales).
//   2) Crear un launcher CSS-only (bubble flotante bottom-right) que abre
//      el iframe al primer click. Mientras el launcher esté cerrado, el
//      iframe NO se carga — facade pattern, evita penalizar LCP del host.
//   3) Al abrir: inyectar un iframe que apunta a frame.html con la config
//      en el query string. El iframe vive en widget.yatendi.com (origin
//      separado) para isolación de CSS+JS+cookies first-party.
//   4) Comunicación host ↔ iframe vía postMessage con prefix 'yatendi:'.
//      Mínimo en este slice: 'yatendi:ready' (iframe avisa que cargó) y
//      'yatendi:close' (iframe pide cerrar al click en X).
//
// Este loader NO depende de bundles del iframe — son piezas independientes.
// El usuario que pega <script> tiene un launcher visible en ~30ms; el
// iframe (más pesado) se carga solo cuando el visitante interactúa.

import { API_BASE } from "../shared/config.ts";

interface WidgetConfig {
  tenant: string;
  base: string; // origin del widget — derivado del src del propio script
  // Contexto renderizado por el sitio (atributo data-context, JSON). A
  // diferencia de identify/track (JS en runtime), esto lo pone el HTML del
  // servidor — Yatendi lo usa para inyectar los precios oficiales del país en
  // la landing. Igual viaja por el browser: el backend lo trata como "lo que
  // el sitio declara", no como verdad verificada.
  siteContext?: Record<string, unknown>;
}

function readConfig(): WidgetConfig | null {
  // El propio script tag que ejecuta este código.
  const scripts = document.querySelectorAll("script[data-tenant]");
  const me = scripts[scripts.length - 1] as HTMLScriptElement | undefined;
  if (!me) {
    console.warn("[yatendi] no se encontró <script data-tenant=...>");
    return null;
  }
  const tenant = me.dataset.tenant;
  if (!tenant) {
    console.warn("[yatendi] data-tenant ausente en el <script>");
    return null;
  }
  // Origin del script — de ahí inferimos frame.html.
  const src = me.src;
  const base = new URL(src).origin;
  // data-context opcional: JSON renderizado por el sitio (ej. precios del país).
  let siteContext: Record<string, unknown> | undefined;
  const rawCtx = me.dataset.context;
  if (rawCtx) {
    try {
      const parsed = JSON.parse(rawCtx);
      if (parsed && typeof parsed === "object") siteContext = parsed;
    } catch {
      // data-context malformado → lo ignoramos, no rompemos el widget.
    }
  }
  return { tenant, base, siteContext };
}

// Versión del contrato loader↔iframe↔backend. Bump al cambiar el shape de
// lo que el loader pasa al iframe. 1c: + ?ctx (inteligencia del visitante)
// y data-tenant ahora es token opaco pk_xxx (HARD-1).
const SLICE = "1c";
const LAUNCHER_ID = "yatendi-launcher";
const FRAME_ID = "yatendi-frame";
const MINIMIZE_ID = "yatendi-minimize";

// Launcher visual: pill horizontal estilo Zipchat AI — avatar circular
// oscuro + texto "Ask AI…" + flecha arrow up. Click en cualquier parte
// abre el iframe. Cuando el iframe está abierto, el pill se oculta (el
// header del iframe tiene su propia X). El bubble lleno con tail estilo
// iOS Messages va dentro del avatar.
const ICON_MESSAGE_FILLED = `<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" style="display:block" aria-hidden="true"><path d="M12 3C6.48 3 2 7.04 2 12c0 1.88.65 3.62 1.74 5.06L2 22l5.13-1.62C8.55 20.78 10.22 21 12 21c5.52 0 10-4.04 10-9s-4.48-9-10-9z"/></svg>`;
const ICON_ARROW_UP = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="display:block" aria-hidden="true"><line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></svg>`;
const ICON_CLOSE_GRAY = `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="display:block" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>`;

// Estado del iframe — el launcher lo lee para decidir qué icon mostrar y
// si su click abre o cierra.
let frameOpen = false;

// ─── Inteligencia del visitante (captura de señales first-party) ─────────
// El loader corre EN el page del cliente, así que puede leer señales que el
// iframe (sandboxed en otro origin) no ve: UTM, referrer, navegación del
// sitio, device, idioma, timezone. NO accede a cookies de terceros ni al
// caché ni al historial del browser (same-origin policy). El LLM + el
// operador usan estas señales para inferir perfil/intención del visitante.

const NAV_KEY_PREFIX = "yatendi:nav:";
const NAV_MAX = 12; // últimas N páginas del sitio que recorrió el visitor

interface VisitedPage {
  u: string; // pathname + search (sin el origin para ahorrar bytes)
  t: string; // document.title recortado
  ts: number; // epoch ms
}

/** Registra la página actual en el recorrido del visitor (localStorage del
 *  page del cliente). Se llama en cada boot — multipage y SPA con history
 *  API quedan cubiertos si el snippet está en todas las páginas. Cap a
 *  NAV_MAX para no inflar el storage ni el query del iframe. */
function trackPageVisit(tenant: string): void {
  try {
    const key = NAV_KEY_PREFIX + tenant;
    const raw = localStorage.getItem(key);
    const nav: VisitedPage[] = raw ? JSON.parse(raw) : [];
    const entry: VisitedPage = {
      u: (window.location.pathname + window.location.search).slice(0, 200),
      t: (document.title || "").slice(0, 120),
      ts: Date.now(),
    };
    // Evitar duplicar si recargó la misma URL seguido.
    const last = nav[nav.length - 1];
    if (!last || last.u !== entry.u) nav.push(entry);
    const trimmed = nav.slice(-NAV_MAX);
    localStorage.setItem(key, JSON.stringify(trimmed));
  } catch {
    // localStorage bloqueado — el recorrido se pierde, no es crítico.
  }
}

function readNav(tenant: string): VisitedPage[] {
  try {
    const raw = localStorage.getItem(NAV_KEY_PREFIX + tenant);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

/** Extrae los UTM params del URL actual (marketing attribution). */
function readUtm(): Record<string, string> {
  const p = new URLSearchParams(window.location.search);
  const utm: Record<string, string> = {};
  for (const k of [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
    "gclid", // Google Ads click id
    "fbclid", // Facebook/Meta click id
  ]) {
    const v = p.get(k);
    if (v) utm[k] = v.slice(0, 120);
  }
  return utm;
}

// ── API pública JS: window.yatendi('identify'|'track', …) ───────────────
// El sitio del cliente le pasa al widget QUIÉN es el visitante (identify) y
// QUÉ hace (track) — la inteligencia accionable de verdad. Ejemplos:
//
//   yatendi('identify', { name:'Juan', email:'j@x.com', plan:'premium' })
//   yatendi('track', 'viewed_product', { sku:'ZAP-1', name:'Botas', price:120 })
//   yatendi('track', 'added_to_cart', { sku:'ZAP-1', qty:1 })
//
// traits → merge (la última identificación gana por campo). events → cola
// (cap EVENTS_MAX, los más recientes). Ambos en localStorage del page para
// sobrevivir navegación, y se incluyen en buildVisitorContext.

const TRAITS_KEY_PREFIX = "yatendi:traits:";
const EVENTS_KEY_PREFIX = "yatendi:events:";
const EVENTS_MAX = 20;

interface TrackedEvent {
  e: string; // nombre del evento
  p: Record<string, unknown>; // props
  ts: number;
}

function readTraits(tenant: string): Record<string, unknown> {
  try {
    const raw = localStorage.getItem(TRAITS_KEY_PREFIX + tenant);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function readEvents(tenant: string): TrackedEvent[] {
  try {
    const raw = localStorage.getItem(EVENTS_KEY_PREFIX + tenant);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

/** Define window.yatendi(cmd, ...args). Idempotente. El sitio del cliente
 *  la llama en cualquier momento; persistimos en localStorage del page.
 *  Si el iframe ya está abierto, propagamos el update via postMessage para
 *  que el backend lo reciba en el próximo mensaje (sin esperar recarga). */
function installPublicApi(config: WidgetConfig): void {
  const tenant = config.tenant;
  const api = (cmd: string, a?: unknown, b?: unknown): void => {
    try {
      if (cmd === "open") {
        // Abre el chat. Opcional { message } → autoenvía un primer mensaje del
        // visitante (lo usan los CTAs del host: "Quiero mi diagnóstico", etc.).
        const msg =
          a && typeof a === "object" &&
          typeof (a as { message?: unknown }).message === "string"
            ? (a as { message: string }).message
            : undefined;
        openFrame(config, msg);
      } else if (cmd === "identify" && a && typeof a === "object") {
        // Merge de traits — sanitizamos a primitivos cortos (no objetos
        // anidados arbitrarios) para no inflar el storage ni el prompt.
        const current = readTraits(tenant);
        for (const [k, v] of Object.entries(a as Record<string, unknown>)) {
          if (v == null) continue;
          if (typeof v === "object") continue;
          current[String(k).slice(0, 40)] = String(v).slice(0, 200);
        }
        localStorage.setItem(TRAITS_KEY_PREFIX + tenant, JSON.stringify(current));
      } else if (cmd === "track" && typeof a === "string") {
        const events = readEvents(tenant);
        const props: Record<string, unknown> = {};
        if (b && typeof b === "object") {
          for (const [k, v] of Object.entries(b as Record<string, unknown>)) {
            if (v == null || typeof v === "object") continue;
            props[String(k).slice(0, 40)] = String(v).slice(0, 120);
          }
        }
        events.push({ e: a.slice(0, 60), p: props, ts: Date.now() });
        localStorage.setItem(
          EVENTS_KEY_PREFIX + tenant,
          JSON.stringify(events.slice(-EVENTS_MAX))
        );
      }
    } catch {
      // localStorage bloqueado — la llamada se pierde, no es crítica.
    }
  };
  // Reemplaza la cola del stub del snippet (si lo hubiera) y procesa
  // llamadas que el sitio haya hecho antes de que cargáramos.
  const existing = (window as any).yatendi;
  (window as any).yatendi = api;
  if (existing && Array.isArray(existing.q)) {
    for (const args of existing.q as unknown[][]) {
      api(args[0] as string, args[1], args[2]);
    }
  }
}

/** Arma el snapshot del contexto del visitante para mandar al backend.
 *  Solo señales first-party legítimas — nada que el browser no exponga
 *  públicamente a la página. El backend parsea el UA (device/os/browser)
 *  y el LLM infiere perfil/gustos a partir de todo esto + el chat. */
function buildVisitorContext(
  tenant: string,
  siteContext?: Record<string, unknown>,
): Record<string, unknown> {
  let tz = "";
  try {
    tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    // Intl puede no estar en browsers muy viejos.
  }
  return {
    landing: window.location.href.slice(0, 300),
    title: (document.title || "").slice(0, 120),
    referrer: document.referrer.slice(0, 300),
    utm: readUtm(),
    lang: navigator.language || "",
    tz,
    ua: (navigator.userAgent || "").slice(0, 300),
    screen: `${window.screen?.width ?? 0}x${window.screen?.height ?? 0}`,
    nav: readNav(tenant),
    // Inteligencia accionable: quién es (identify) + qué hizo (track).
    traits: readTraits(tenant),
    events: readEvents(tenant),
    // Contexto declarado por el sitio (data-context) — ej. precios oficiales
    // del país. El backend lo lee en visitorContext.site.
    ...(siteContext ? { site: siteContext } : {}),
  };
}

// ─── Posicionamiento (drag + snap a esquina) ─────────────────────────────
// El visitor puede arrastrar el launcher (cerrado) o el minimize button
// (abierto) y al soltar el widget snapea a la esquina más cercana. La
// elección persiste en localStorage por tenant. Default: bottom-center.

// "bottom-center" = posición por DEFAULT (abajo y centrado horizontalmente).
// El resto son las 4 esquinas a las que el visitante puede arrastrar.
type Corner =
  | "bottom-center"
  | "bottom-right"
  | "bottom-left"
  | "top-right"
  | "top-left";
const CORNERS: ReadonlySet<Corner> = new Set<Corner>([
  "bottom-center",
  "bottom-right",
  "bottom-left",
  "top-right",
  "top-left",
]);
// Mitad del ancho de cada pieza, para centrar con `left: calc(50% - half)`
// (evita `transform: translateX` que chocaría con el hover del launcher).
const LAUNCHER_HALF = 150; // launcher 300px
const FRAME_HALF = 220; // iframe 440px
const MINIMIZE_HALF = 25; // botón minimize 50px

// ── Dark glass (estética de la landing) ──────────────────────────────────
// Mismo "fondo type" que la landing (header + banner de cookies): slate oscuro
// translúcido + blur + saturate. Sobre oscuro, el contenido del pill va en
// claro. El inset top highlight da el filo de vidrio sutil.
const GLASS_BG = "rgba(11,15,23,0.6)"; // más translúcido (se ve más lo de atrás)
const GLASS_BLUR = "blur(12px) saturate(140%)";
const GLASS_BORDER = "1px solid rgba(231,236,243,0.12)";
const GLASS_SHADOW =
  "0 12px 36px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.08)";
const GLASS_SHADOW_HOVER =
  "0 16px 44px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.14)";
const EDGE = 20; // px de margen al borde de la viewport
const LAUNCHER_H = 60; // alto del pill cerrado
const SPACING = 10; // separación entre launcher y iframe abierto

// Breakpoint mobile: por debajo de esto el iframe va a pantalla completa.
// Un panel flotante con offset + 85vh deja el header fuera del viewport
// visible cuando la barra de URL del browser está presente (problema
// clásico de 100vh en mobile). Como Intercom/Crisp/Zipchat: fullscreen.
const MOBILE_MAX_PX = 480;
function isMobile(): boolean {
  try {
    return window.matchMedia(`(max-width: ${MOBILE_MAX_PX}px)`).matches;
  } catch {
    return window.innerWidth <= MOBILE_MAX_PX;
  }
}

// Posición de "ancla" actual — sincronizada entre launcher, iframe,
// minimize button. Se inicializa con la corner guardada.
let currentCorner: Corner = "bottom-center";

function loadCorner(tenant: string): Corner {
  try {
    const v = localStorage.getItem(`yatendi:corner:${tenant}`);
    if (v && CORNERS.has(v as Corner)) return v as Corner;
  } catch {
    // localStorage bloqueado (private mode estricto, cookies disabled, etc.)
    // — caemos al default.
  }
  return "bottom-center";
}

function saveCorner(tenant: string, corner: Corner): void {
  try {
    localStorage.setItem(`yatendi:corner:${tenant}`, corner);
  } catch {
    // Fail-soft: si no podemos persistir, el visitor pierde la posición al
    // refrescar. Tradeoff aceptable vs romper el widget entero.
  }
}

/** Resetea top/bottom/left/right y aplica solo los del corner activo. */
function clearAnchor(el: HTMLElement): void {
  el.style.top = "";
  el.style.bottom = "";
  el.style.left = "";
  el.style.right = "";
}

function applyCornerToLauncher(el: HTMLElement, corner: Corner): void {
  clearAnchor(el);
  if (corner.startsWith("top")) el.style.top = `${EDGE}px`;
  else el.style.bottom = `${EDGE}px`;
  if (corner === "bottom-center") el.style.left = `calc(50% - ${LAUNCHER_HALF}px)`;
  else if (corner.endsWith("left")) el.style.left = `${EDGE}px`;
  else el.style.right = `${EDGE}px`;
}

function applyCornerToFrame(el: HTMLElement, corner: Corner): void {
  clearAnchor(el);
  if (isMobile()) {
    // Fullscreen en mobile. OJO: un <iframe> es un *replaced element* — con
    // width/height en `auto` colapsa a su tamaño intrínseco (300×150) e
    // ignora los insets, NO se estira. Por eso van dimensiones EXPLÍCITAS.
    // height en 100dvh (dynamic viewport height) excluye la barra de URL del
    // browser, así el header NUNCA queda cortado; fallback a 100vh en
    // browsers viejos (el navegador ignora la 2da asignación si no soporta
    // dvh y se queda con vh).
    el.style.top = "0";
    el.style.left = "0";
    el.style.right = "";
    el.style.bottom = "";
    el.style.width = "100%";
    el.style.height = "100vh";
    el.style.height = "100dvh";
    el.style.minHeight = "0";
    el.style.maxWidth = "none";
    el.style.maxHeight = "none";
    el.style.borderRadius = "0";
    return;
  }
  // Desktop: panel flotante anclado a la esquina. Restauramos las dimensiones
  // (por si venimos de una rotación/resize que lo había puesto fullscreen).
  el.style.width = "440px";
  el.style.height = "85vh";
  el.style.minHeight = "560px";
  el.style.maxWidth = "calc(100vw - 40px)";
  el.style.maxHeight = "calc(100vh - 110px)";
  el.style.borderRadius = "16px";
  // El iframe se posiciona del MISMO lado que el launcher, pero corrido
  // EDGE + LAUNCHER_H + SPACING para no taparlo.
  const offset = EDGE + LAUNCHER_H + SPACING;
  if (corner.startsWith("top")) el.style.top = `${offset}px`;
  else el.style.bottom = `${offset}px`;
  if (corner === "bottom-center") el.style.left = `calc(50% - ${FRAME_HALF}px)`;
  else if (corner.endsWith("left")) el.style.left = `${EDGE}px`;
  else el.style.right = `${EDGE}px`;
}

function applyCornerToMinimize(el: HTMLElement, corner: Corner): void {
  clearAnchor(el);
  // El minimize button vive donde estaba el launcher (misma esquina).
  // Está centrado horizontalmente al iframe — el offset desde el borde
  // queda igual al del launcher (EDGE).
  if (corner.startsWith("top")) el.style.top = `${EDGE}px`;
  else el.style.bottom = `${EDGE}px`;
  if (corner === "bottom-center") {
    // El iframe está centrado en la viewport → el minimize también.
    el.style.left = `calc(50% - ${MINIMIZE_HALF}px)`;
    return;
  }
  // Centrado al iframe: mitad iframe (220) - mitad btn (25) = 195 desde el
  // borde "lateral". Equivalente al `calc(20 + 210 - 25)` original.
  const lateral = EDGE + 220 - 25;
  if (corner.endsWith("left")) el.style.left = `${lateral}px`;
  else el.style.right = `${lateral}px`;
}

/** Recalcula la posición de TODOS los elementos del widget según el
 *  corner activo. Idempotente — segura de llamar varias veces. */
function syncCorner(): void {
  const launcher = document.getElementById(LAUNCHER_ID);
  if (launcher) applyCornerToLauncher(launcher, currentCorner);
  const frame = document.getElementById(FRAME_ID);
  if (frame) applyCornerToFrame(frame, currentCorner);
  const min = document.getElementById(MINIMIZE_ID);
  if (min) applyCornerToMinimize(min, currentCorner);
}

/** Snap a la posición más cercana al CENTRO del elemento arrastrado. Usar el
 *  centro evita el flicker de "casi mitad" donde una posición gana por 1px.
 *  Si quedó en la BANDA CENTRAL horizontal (tercio medio) y abajo → vuelve a
 *  bottom-center; sino, a la esquina del cuadrante. */
function nearestCorner(el: HTMLElement): Corner {
  const r = el.getBoundingClientRect();
  const cx = r.left + r.width / 2;
  const cy = r.top + r.height / 2;
  const w = window.innerWidth;
  const isBottom = cy >= window.innerHeight / 2;
  if (isBottom && cx > w / 3 && cx < (w * 2) / 3) return "bottom-center";
  const isLeft = cx < w / 2;
  return `${isBottom ? "bottom" : "top"}-${isLeft ? "left" : "right"}` as Corner;
}

/** Hace draggable un elemento. Click corto (sin movimiento) sigue
 *  disparando los onclick existentes. Drag (movimiento > 5px) bloquea el
 *  próximo click y al soltar snapea a la esquina más cercana. Soporta
 *  mouse + touch. */
function makeDraggable(el: HTMLElement, tenant: string): void {
  let dragging = false;
  let pointerOffsetX = 0;
  let pointerOffsetY = 0;
  let moved = false;
  const DRAG_THRESHOLD = 5;

  function getPoint(e: MouseEvent | TouchEvent): { x: number; y: number } | null {
    if ("touches" in e) {
      const t = e.touches[0] ?? e.changedTouches[0];
      return t ? { x: t.clientX, y: t.clientY } : null;
    }
    return { x: e.clientX, y: e.clientY };
  }

  function onStart(e: MouseEvent | TouchEvent) {
    const p = getPoint(e);
    if (!p) return;
    const r = el.getBoundingClientRect();
    dragging = true;
    moved = false;
    pointerOffsetX = p.x - r.left;
    pointerOffsetY = p.y - r.top;
    el.style.transition = "none";
    document.body.style.userSelect = "none";
  }

  function onMove(e: MouseEvent | TouchEvent) {
    if (!dragging) return;
    const p = getPoint(e);
    if (!p) return;
    const r = el.getBoundingClientRect();
    const dx = p.x - (r.left + pointerOffsetX);
    const dy = p.y - (r.top + pointerOffsetY);
    if (!moved && Math.abs(dx) + Math.abs(dy) > DRAG_THRESHOLD) {
      moved = true;
    }
    if (!moved) return;
    // Cambiar de "anchor por corner" a posición pixel-perfect mientras
    // drageamos. Limpiamos right/bottom para que top/left manden.
    clearAnchor(el);
    el.style.left = `${p.x - pointerOffsetX}px`;
    el.style.top = `${p.y - pointerOffsetY}px`;
    if ("preventDefault" in e) e.preventDefault();
  }

  function onEnd() {
    if (!dragging) return;
    dragging = false;
    document.body.style.userSelect = "";
    el.style.transition = "";
    if (!moved) return; // click normal — onclick original se dispara
    // Snap a esquina + persistir + sincronizar todo el widget.
    const corner = nearestCorner(el);
    currentCorner = corner;
    saveCorner(tenant, corner);
    syncCorner();
    // Bloquear el próximo click para que no abra el iframe accidentalmente
    // al terminar el drag (mousedown → mousemove → mouseup → click).
    el.addEventListener(
      "click",
      (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
      },
      { capture: true, once: true },
    );
  }

  el.addEventListener("mousedown", onStart);
  el.addEventListener("touchstart", onStart, { passive: true });
  document.addEventListener("mousemove", onMove);
  document.addEventListener("touchmove", onMove, { passive: false });
  document.addEventListener("mouseup", onEnd);
  document.addEventListener("touchend", onEnd);
}

function injectLauncher(config: WidgetConfig): void {
  // Idempotente: si ya hay un launcher, no duplicar.
  if (document.getElementById(LAUNCHER_ID)) return;

  // Pill horizontal: avatar + "Ask AI…" + send arrow. Toda la pill es
  // clickeable; el send arrow es visual (mismo target click). En slice
  // futuro: el placeholder se transforma en input real, el visitor
  // escribe y al apretar send abre el iframe Y manda el mensaje.
  const launcher = document.createElement("button");
  launcher.id = LAUNCHER_ID;
  launcher.type = "button";
  launcher.setAttribute("aria-label", "Abrir chat");

  Object.assign(launcher.style, {
    position: "fixed",
    // top/right/bottom/left se aplican via applyCornerToLauncher abajo —
    // mantener position:fixed para que el corner mande.
    width: "300px",
    maxWidth: "calc(100vw - 40px)",
    height: "60px",
    padding: "8px 8px 8px 8px",
    borderRadius: "30px",
    // Liquid glass: ver constantes GLASS_*. La prop -webkit- (Safari/iOS) se
    // setea abajo con setProperty (no está en el tipo de CSSStyleDeclaration).
    border: GLASS_BORDER,
    background: GLASS_BG,
    backdropFilter: GLASS_BLUR,
    color: "#e7ecf3",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    boxShadow: GLASS_SHADOW,
    zIndex: "2147483646",
    transition: "transform 150ms ease, box-shadow 150ms ease",
    fontFamily: "Outfit, -apple-system, BlinkMacSystemFont, sans-serif",
    fontSize: "15px",
    textAlign: "left",
  } satisfies Partial<CSSStyleDeclaration>);
  // Prefijo -webkit- (Safari/iOS) via setProperty: no está en el tipo de
  // CSSStyleDeclaration, pero el browser lo aplica igual.
  launcher.style.setProperty("-webkit-backdrop-filter", GLASS_BLUR);

  launcher.addEventListener("mouseenter", () => {
    launcher.style.transform = "translateY(-1px)";
    launcher.style.boxShadow = GLASS_SHADOW_HOVER;
  });
  launcher.addEventListener("mouseleave", () => {
    launcher.style.transform = "translateY(0)";
    launcher.style.boxShadow = GLASS_SHADOW;
  });

  launcher.addEventListener("click", () => openFrame(config));

  // Estructura interna sobre el vidrio OSCURO: avatar teal de marca + texto
  // placeholder claro + send arrow translúcido. Inline styles para mantener el
  // bundle del loader autónomo sin un <style>.
  launcher.innerHTML = `
    <span style="
      width:44px;height:44px;border-radius:50%;
      background:#00d2aa;color:#001f1a;
      display:grid;place-items:center;flex-shrink:0;">
      ${ICON_MESSAGE_FILLED}
    </span>
    <span style="
      flex:1;color:#aab4c2;font-size:15px;line-height:1;
      font-family:inherit;">¿En qué te ayudo?</span>
    <span style="
      width:36px;height:36px;border-radius:50%;
      background:rgba(255,255,255,0.08);color:#e7ecf3;
      display:grid;place-items:center;flex-shrink:0;">
      ${ICON_ARROW_UP}
    </span>
  `;
  applyCornerToLauncher(launcher, currentCorner);
  makeDraggable(launcher, config.tenant);
  document.body.appendChild(launcher);
}

/** Sincroniza la visibilidad del launcher pill + el botón minimize.
 *  Estado:
 *    - Iframe cerrado → pill visible, minimize oculto.
 *    - Iframe abierto → pill oculta, minimize visible (círculo blanco
 *      con X centrado abajo, como Zipchat).
 *  El minimize button se monta lazy en la primera apertura del iframe. */
function syncLauncherIcon(): void {
  const launcher = document.getElementById(LAUNCHER_ID);
  if (launcher) launcher.style.display = frameOpen ? "none" : "flex";

  const min = document.getElementById(MINIMIZE_ID);
  if (min) min.style.display = frameOpen ? "grid" : "none";
}

/** Crea el botón minimize. Se llama en la primera apertura del iframe.
 *  Idempotente — si ya existe, no duplica. Click → mismo handler que la
 *  X del header del iframe (closeFrame). */
function injectMinimizeButton(tenant: string): void {
  if (document.getElementById(MINIMIZE_ID)) return;

  const btn = document.createElement("button");
  btn.id = MINIMIZE_ID;
  btn.type = "button";
  btn.setAttribute("aria-label", "Minimizar chat");
  btn.innerHTML = ICON_CLOSE_GRAY;

  // Centrado horizontal respecto al ancho del iframe (que está alineado
  // a la derecha con `right: 20px` + width 420px). El minimize está
  // pegado abajo a la derecha tambien, en el lugar donde antes estaba
  // la pill — para que el ojo del visitor lo encuentre sin moverse.
  Object.assign(btn.style, {
    position: "fixed",
    // top/right/bottom/left se aplican via applyCornerToMinimize abajo.
    width: "50px",
    height: "50px",
    borderRadius: "50%",
    // Liquid glass, igual que el launcher (ver constantes GLASS_*).
    border: GLASS_BORDER,
    background: GLASS_BG,
    backdropFilter: GLASS_BLUR,
    color: "#aab4c2",
    cursor: "pointer",
    display: "none", // se activa via syncLauncherIcon
    placeItems: "center",
    boxShadow: GLASS_SHADOW,
    zIndex: "2147483646", // un menos que el iframe, igual que la pill
    transition: "transform 150ms ease, color 150ms ease",
    padding: "0",
    lineHeight: "0",
  } satisfies Partial<CSSStyleDeclaration>);
  btn.style.setProperty("-webkit-backdrop-filter", GLASS_BLUR);

  btn.addEventListener("mouseenter", () => {
    btn.style.transform = "scale(1.08)";
    btn.style.color = "#e7ecf3";
  });
  btn.addEventListener("mouseleave", () => {
    btn.style.transform = "scale(1)";
    btn.style.color = "#aab4c2";
  });

  btn.addEventListener("click", () => closeFrame());

  applyCornerToMinimize(btn, currentCorner);
  makeDraggable(btn, tenant);
  document.body.appendChild(btn);
}

function openFrame(config: WidgetConfig, initialMsg?: string): void {
  // Lazy-inject el minimize button en la primera apertura.
  injectMinimizeButton(config.tenant);

  // Iframe ya montado: solo lo mostramos.
  let frame = document.getElementById(FRAME_ID) as HTMLIFrameElement | null;
  if (frame) {
    frame.style.display = "block";
    frameOpen = true;
    // Re-aplicar layout: pudo haber cambiado de mobile↔desktop (rotación,
    // resize de ventana) entre el primer open y este.
    syncCorner();
    syncLauncherIcon();
    // Mensaje inicial cuando el iframe YA existía (segundo CTA): se lo pasamos
    // por postMessage al origin del widget; el frame lo autoenvía.
    if (initialMsg) {
      try {
        frame.contentWindow?.postMessage(
          { type: "yatendi:ask", text: initialMsg },
          new URL(config.base).origin,
        );
      } catch {
        /* config.base inválida — no rompemos la apertura */
      }
    }
    return;
  }

  frame = document.createElement("iframe");
  frame.id = FRAME_ID;
  frame.title = "Chat con Yatendi";
  // Pasamos el hostname del HOST site (donde corre el loader) al iframe
  // como query param. El iframe lo reenvía al backend para validar
  // allowedDomains. El Origin header del fetch desde dentro del iframe
  // es el dominio del widget (CDN), NO el del cliente que pegó el
  // snippet — sin este pasaje, el backend nunca valida correcto el
  // dominio del cliente.
  const host = encodeURIComponent(window.location.hostname);
  // Contexto de inteligencia del visitante: señales objetivas del page
  // (UTM/referrer/landing, device, idioma, tz, navegación del sitio). El
  // loader las captura porque corre EN el page del cliente; el iframe está
  // sandboxed en otro origin y no puede leerlas. Va comprimido en el query
  // — el iframe lo reenvía en POST /v1/sessions.
  const ctx = encodeURIComponent(
    JSON.stringify(buildVisitorContext(config.tenant, config.siteContext)),
  );
  // Mensaje inicial (CTA del host) en el primer montaje: el frame lo lee de
  // ?msg= y lo autoenvía apenas crea la sesión.
  const msgParam = initialMsg ? `&msg=${encodeURIComponent(initialMsg)}` : "";
  frame.src = `${config.base}/frame.html?t=${encodeURIComponent(config.tenant)}&s=${SLICE}&host=${host}&ctx=${ctx}${msgParam}`;
  Object.assign(frame.style, {
    position: "fixed",
    // top/right/bottom/left se aplican via applyCornerToFrame abajo.
    // 440px ancho da espacio cómodo para mensajes con Markdown rico
    // (bullets, bold inline). 85vh aprovecha bien pantallas altas; el
    // max-height previene desbordes en laptops chicas. minHeight 560px
    // garantiza altura usable incluso en displays bajos.
    width: "440px",
    height: "85vh",
    minHeight: "560px",
    maxWidth: "calc(100vw - 40px)",
    maxHeight: "calc(100vh - 110px)",
    border: "none",
    borderRadius: "16px",
    boxShadow: "0 16px 48px rgba(0,0,0,0.18)",
    zIndex: "2147483647", // max — siempre arriba
    background: "white",
    colorScheme: "light", // evita herencia del dark-mode del host
  } satisfies Partial<CSSStyleDeclaration>);
  applyCornerToFrame(frame, currentCorner);
  document.body.appendChild(frame);
  frameOpen = true;
  syncLauncherIcon();
}

function closeFrame(): void {
  const frame = document.getElementById(FRAME_ID);
  if (frame) frame.style.display = "none";
  frameOpen = false;
  syncLauncherIcon();
}

function setupHostListener(): void {
  window.addEventListener("message", (ev) => {
    // Validar origen y prefijo del mensaje. Cualquier site con un iframe
    // puede mandar postMessage al window padre — chequear origin antes.
    if (typeof ev.data !== "object" || ev.data === null) return;
    const data = ev.data as { type?: string };
    if (typeof data.type !== "string" || !data.type.startsWith("yatendi:")) {
      return;
    }
    switch (data.type) {
      case "yatendi:ready":
        // Iframe listo — podríamos mostrar un badge "1 mensaje nuevo" etc.
        // Por ahora no hay state que sincronizar.
        break;
      case "yatendi:close":
        closeFrame();
        break;
      default:
        // Mensaje yatendi:* que no manejamos — ignorar.
        break;
    }
  });
}

/** Consulta GET /v1/config para saber si el canal está habilitado para
 *  este host. Devuelve:
 *    true  → mostrar el launcher (enabled && host permitido).
 *    false → ocultar (deshabilitado o dominio no autorizado).
 *  Fail-OPEN: si el fetch falla (red/timeout), devolvemos true — el
 *  enforcement real vive en POST /v1/sessions, así que un blip de red no
 *  oculta un widget legítimamente habilitado. Solo ocultamos ante un
 *  `show:false` EXPLÍCITO del backend. */
async function shouldShowWidget(tenant: string): Promise<boolean> {
  try {
    const host = encodeURIComponent(window.location.hostname);
    const res = await fetch(
      `${API_BASE}/v1/config?t=${encodeURIComponent(tenant)}&host=${host}`,
      { method: "GET" }
    );
    if (!res.ok) return true; // fail-open ante error del server
    const data = (await res.json()) as { show?: boolean };
    return data.show !== false; // solo ocultar si dice explícito false
  } catch {
    return true; // fail-open ante error de red
  }
}

function boot(): void {
  const config = readConfig();
  if (!config) return;
  // Restaurar la esquina elegida por el visitor en una sesión previa
  // (drag → snap → localStorage). Default bottom-right si nunca arrastró
  // o si localStorage está bloqueado.
  currentCorner = loadCorner(config.tenant);
  // Exponer window.yatendi('identify'|'track', …) cuanto antes para
  // capturar llamadas que el sitio haga al cargar (incluido el stub q[]).
  installPublicApi(config);
  // Registrar esta página en el recorrido del visitor (inteligencia). Se
  // hace SIEMPRE, aunque el widget esté oculto — así cuando finalmente
  // abra el chat (o vuelva otro día) tenemos su navegación acumulada.
  trackPageVisit(config.tenant);
  setupHostListener();

  // Re-sincronizar el layout en resize/rotación: cruzar el breakpoint mobile
  // alterna entre fullscreen y panel flotante. syncCorner es idempotente y
  // solo toca elementos montados, así que es barato aunque el chat esté
  // cerrado. orientationchange dispara resize en browsers modernos, pero lo
  // escuchamos explícito por iOS Safari viejo.
  window.addEventListener("resize", syncCorner);
  window.addEventListener("orientationchange", syncCorner);

  // Antes de montar el launcher consultamos si el canal está habilitado.
  // Si el dueño lo deshabilitó (web_widget.enabled=false) o el host no
  // está en allowedDomains, el widget NO aparece en el sitio.
  const mount = () => {
    void shouldShowWidget(config.tenant).then((show) => {
      if (show) injectLauncher(config);
    });
  };

  // Diferir hasta que el DOM esté listo — evita race en sitios donde el
  // <script> está en el <head> sin defer/async.
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", mount);
  } else {
    mount();
  }
}

boot();

// Forzar module scope (sin imports/exports el archivo es "script global"
// y choca con otros archivos del mismo program — typecheck falla).
export {};
